import {useCallback, useRef, useState} from 'react';
import {message, RadioChangeEvent} from 'antd';
import {Moment} from 'moment';
import {DownloadInfoItem} from "../../../../types/analytics";
import useAuthStore from "../../../../store/authStore";
import useHomeStore from "../../../../store/homeStore";
import {useActiveMarker, useMarkerStore} from "../../../../store/markerStore";
import useDatasetStore from "../../../../store/datasetStore";
import {usePointStore} from "../../../../store/pointStore";
import {EVENT_DATA_DOWNLOADED, trackUpdate} from "../../../../utils/matomoEvents";
import {DTFullFormat} from "../../../../utils/StaticVariable";
import {createDownloadOrderString} from "../../../../utils/createDownloadOrderString";
import Api from "../../../../api/Api";
import {CheckboxValueType} from "antd/lib/checkbox/Group";

import {DownloadOption, DownloadType} from '../../../../types/metadata'
import {downloadOptions2DSpectra, downloadOptionsParameters} from "../../../../utils/common";

export const useDatasetDownload = (
  item: DownloadInfoItem,
  coords: number[],
  label: string,
  namePanel: string,
  price: number,
  setDatasetIsOfflineWarningVisible: (state: boolean) => void
) => {
  const [showCancelDownloadButtonParameters, setShowCancelDownloadButtonParameters] = useState<boolean>(false);
  const [showCancelDownloadButton2DSpectre, setShowCancelDownloadButton2DSpectre] = useState<boolean>(false);
  const [checkPolicy, setCheckPolicy] = useState<boolean>(false);
  const [showWarn, setShowWarn] = useState<boolean>(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState<boolean>(false);
  const [isDownloadSpectraModalVisible, setIsDownloadSpectraModalVisible] = useState<boolean>(false);
  const [downloadValues, setDownloadValues] = useState<CheckboxValueType[]>([]);
  const [downloadOptions, setDownloadOptions] = useState<DownloadOption[]>([]);
  const [downloadType, setDownloadType] = useState<DownloadType | null>(null);
  const [disableDownload, setDisableDownload] = useState<boolean>(false)

  const abortControllerRef = useRef<AbortController | null>(null);

  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setShowSpectraModal = useHomeStore(state => state.setShowSpectraModal);

  const point = useActiveMarker();
  const datasetBriefs = useDatasetStore(state => state.datasetBriefs);
  const fwePoints = usePointStore(state => state.fwePoints);

  const handlePolicyChange = useCallback((e: RadioChangeEvent): void => {
    setCheckPolicy(e.target.checked);
    setShowWarn(false);
  }, []);

  const handleChangeDownloadOptions = useCallback((checkedList: CheckboxValueType[]): void => {
    setDownloadValues(checkedList);
  }, []);

  const resetDownloadProcess = useCallback((): void => {
    setDownloadType(null);
    setDownloadValues([]);
    setDownloadOptions([]);
  }, []);

  const handleDownloadModalCancel = useCallback((): void => {
    setIsDownloadModalVisible(false);
    resetDownloadProcess();
  }, [resetDownloadProcess]);


  const handleDownload = useCallback((typeInput: DownloadType, datasetIsOffline: boolean): void => {
    if (!checkPolicy) {
      setShowWarn(true);
      return;
    }

    const download = async () => {
      setDisableDownload(true);
      setShowWarn(false);
      handleDownloadModalCancel();

      if (datasetIsOffline) {
        setDatasetIsOfflineWarningVisible(true);
        return;
      }

      if (point === undefined || !point?.label) {
        message.warn('Please give the active point a name');
        return;
      }

      if (point) {
        trackUpdate(EVENT_DATA_DOWNLOADED, coords, item.datasetId, "");
        const fileFormats = downloadValues.join('|');
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        let result = null;
        if (typeInput === 'parameters') {
          setShowCancelDownloadButtonParameters(true);

          const extractSubset = await Api.service.createSubsetExtractionTask(
            item.datasetId,
            (item.dateRange?.[0] as Moment).format(DTFullFormat),
            (item.dateRange?.[1] as Moment).format(DTFullFormat),
            coords,
            point?.label ?? '',
            point?.cellSize?.[namePanel]?.toString() ?? '',
            point?.waterDepth?.[namePanel]?.toString() ?? '',
            datasetBriefs,
            fwePoints,
          );

          const shoppingCardDetails = createDownloadOrderString(
            item,
            label,
            coords,
            fileFormats ? fileFormats : 'csv',
            price
          );

          const extractionRequestIds = [extractSubset.Result.OrderIdList[0]];

          result = await Api.moodV2Order.getDirectDownload(
            signal,
            shoppingCardDetails,
            fileFormats ? fileFormats : 'csv',
            extractionRequestIds,
            true
          );

          if (signal && signal.aborted) {
            return;
          }

          if (result.Result !== null) {
            const a = document.createElement('a');
            a.href = result.Result;
            a.click();
          }
        }

        if (typeInput === '2DSpectra') {
          setShowCancelDownloadButton2DSpectre(true);

          const datasetName = item.datasetId;
          const startDate = (item.dateRange?.[0] as Moment).format(DTFullFormat);
          const endDate = (item.dateRange?.[1] as Moment).format(DTFullFormat);
          const location = coords.toString();
          const pointName = point.label;
          const gridCellSize = point?.cellSize?.[namePanel]?.toString() ?? '';
          const waterDepth = point?.waterDepth?.[namePanel]?.toString() ?? '';

          result = await Api.moodV2Order.createWavesSpectraData(
            signal,
            datasetName,
            startDate,
            endDate,
            location,
            pointName,
            gridCellSize,
            waterDepth,
            fileFormats
          );

          if (signal && signal.aborted) {
            return;
          }

          if (result.Result) {
            setIsDownloadSpectraModalVisible(true);
          }
        }

        setDisableDownload(false);
        setShowCancelDownloadButtonParameters(false);
        setShowCancelDownloadButton2DSpectre(false);
      }
    };

    download();
  }, [
    checkPolicy,
    item,
    coords,
    label,
    namePanel,
    price,
    point,
    datasetBriefs,
    fwePoints,
    downloadValues,
    handleDownloadModalCancel,
    setDisableDownload,
    setDatasetIsOfflineWarningVisible
  ]);

  const handleCancelDownload = useCallback((type: DownloadType): void => {
    abortControllerRef.current && abortControllerRef.current.abort();

    if (type === 'parameters') {
      setShowCancelDownloadButtonParameters(false);
    }
    if (type === '2DSpectra') {
      setShowCancelDownloadButton2DSpectre(false);
    }

    setDisableDownload(false);
  }, [setDisableDownload]);

  const handleStartDownloadProcess = useCallback((type: DownloadType): void => {
    if (type === 'parameters') {
      setDownloadValues(['csv']);
      setDownloadOptions(downloadOptionsParameters);
    }
    if (type === '2DSpectra') {
      setDownloadValues(['nc']);
      setDownloadOptions(downloadOptions2DSpectra);
    }

    setDownloadType(type);
    setIsDownloadModalVisible(true);
  }, [downloadOptionsParameters, downloadOptions2DSpectra]);

  const handle2DSpectraDownload = useCallback((startDownloadAction: () => void): void => {
    if (!isAuthenticated()) {
      setShowSpectraModal(true);
      return;
    }
    startDownloadAction();
  }, [isAuthenticated, setShowSpectraModal]);

  return {
    downloadValues,
    downloadOptions,
    downloadType,
    disableDownload,
    isDownloadModalVisible,
    showWarn,
    checkPolicy,
    isDownloadSpectraModalVisible,
    showCancelDownloadButtonParameters,
    showCancelDownloadButton2DSpectre,

    handleChangeDownloadOptions,
    handlePolicyChange,
    handleDownloadModalCancel,
    resetDownloadProcess,
    setIsDownloadSpectraModalVisible,
    setShowWarn,

    handleDownload,
    handleCancelDownload,
    handleStartDownloadProcess,
    handle2DSpectraDownload,

    downloadOptionsParameters,
    downloadOptions2DSpectra,

    abortControllerRef
  };
}
